import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onOpenContactForm }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const services = [
    { name: "Trademark Registration", path: "/services/trademark-registration" },
    { name: "Copyright Registration", path: "/services/copyright-registration" },
    { name: "Company Formation (LLC)", path: "/services/company-formation" },
    { name: "Trademark Search", path: "/services/trademark-search" },
    { name: "Amazon Brand Registry", path: "/services/amazon-brand-registry" }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    // Check if we're on the homepage
    if (location.pathname !== '/') {
      // If not on homepage, navigate to homepage first, then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If on homepage, just scroll
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#232F3E] text-white shadow-md">
      {/* Top Bar */}
      <div className="bg-[#131921] py-2">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-end text-sm">
            <div className="hidden md:flex items-center space-x-6">
              <a href="tel:+18005551234" className="flex items-center text-gray-300 hover:text-white transition-colors">
                <Phone className="w-4 h-4 mr-1" />
                (800) 555-1234
              </a>
              <span className="text-gray-300">Help & Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => navigate('/')} className="flex items-center space-x-3 cursor-pointer">
            <img 
              src="/logo-white.png" 
              alt="Amazon Trademark Registration" 
              className="h-12 w-auto"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <button onClick={() => scrollToSection('home')} className="text-white hover:text-[#FF9900] transition-colors font-medium">
              Home
            </button>
            
            {/* Services - Direct scroll to section */}
            <button 
              onClick={() => scrollToSection('services')}
              className="text-white hover:text-[#FF9900] transition-colors font-medium"
            >
              Services
            </button>
            
            <button onClick={() => scrollToSection('pricing')} className="text-white hover:text-[#FF9900] transition-colors font-medium">
              Pricing
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-[#FF9900] transition-colors font-medium">
              Testimonials
            </button>
            <button onClick={() => scrollToSection('faq')} className="text-white hover:text-[#FF9900] transition-colors font-medium">
              FAQ
            </button>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <Button onClick={onOpenContactForm} className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-black font-semibold">
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-700 pt-4">
            <nav className="flex flex-col space-y-3">
              <button onClick={() => scrollToSection('home')} className="text-white hover:text-[#FF9900] transition-colors text-left font-medium">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="text-white hover:text-[#FF9900] transition-colors text-left font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-white hover:text-[#FF9900] transition-colors text-left font-medium">
                Pricing
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-[#FF9900] transition-colors text-left font-medium">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-white hover:text-[#FF9900] transition-colors text-left font-medium">
                FAQ
              </button>
              <div className="pt-2">
                <a href="tel:+18005551234" className="flex items-center text-gray-300 mb-3">
                  <Phone className="w-4 h-4 mr-2" />
                  (800) 555-1234
                </a>
                <Button onClick={onOpenContactForm} className="w-full bg-[#FF9900] hover:bg-[#FF9900]/90 text-black font-semibold">
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
